// src/pages/ContactSubmissionsPage.js

import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
// --- Import the NEW API functions ---
import { fetchContactSubmissions, deleteContactSubmission } from '../../api/api';
import DataTable from '../DataTable/DataTable';
import Pagination from '../Pagination/Pagination';
import Modal from '../Modal/Modal';
import Spinner from '../../assets/spinner.svg';

// Use the same CSS file for consistent styling
import './OrdersPage.css';

const ContactSubmissionsPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] =  useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const loadSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchContactSubmissions(currentPage);
      setSubmissions(data.results);
      setTotalPages(data.totalPages);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch contact submissions. Please try again later.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleView = (item) => {
    // A "View" action is more appropriate than "Edit" for a contact message
    Swal.fire({
        title: `Message from ${item.firstName || 'N/A'} ${item.lastName || ''}`,
        html: `
            <div style="text-align: left; padding: 1rem;">
                <p><strong>Email:</strong> <a href="mailto:${item.email}">${item.email}</a></p>
                <p><strong>Phone:</strong> ${item.phone || 'Not Provided'}</p>
                <hr style="margin: 1rem 0;" />
                <p><strong>Message:</strong></p>
                <p style="white-space: pre-wrap; background: #f8f9fa; padding: 0.5rem; border-radius: 4px;">${item.message}</p>
            </div>
        `,
        width: '600px'
    });
  };

  const handleDeleteRequest = (id) => {
    setItemToDelete(id);
    setIsModalOpen(true);
  };
  
  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        await deleteContactSubmission(itemToDelete);
        Swal.fire('Deleted!', 'The submission has been deleted.', 'success');
        setIsModalOpen(false);
        setItemToDelete(null);
        // Refresh the data after deletion
        loadSubmissions(); 
      } catch (err) {
        Swal.fire('Error!', 'Failed to delete the submission.', 'error');
      }
    }
  };

  // --- DEFINE THE COLUMNS for the contact submissions table ---
  // This definition is now fully supported by the updated DataTable component.
  const columns = [
    { 
        header: 'Name', 
        accessor: (row) => `${row.firstName || ''} ${row.lastName || ''}`.trim() || 'N/A' 
    },
    { header: 'Email', accessor: 'email' },
    { header: 'Phone', accessor: 'phone' },
    { 
        header: 'Message', 
        accessor: (row) => row.message.length > 50 ? `${row.message.substring(0, 50)}...` : row.message 
    },
    { header: 'Date Received', accessor: 'createdAt', isDate: true },
  ];

  const actionButtonClasses = {
    edit: 'nfc-action-btn nfc-view-btn', // Changed to a view button
    delete: 'nfc-action-btn nfc-delete-btn',
    wrapper: 'nfc-action-buttons'
  };

  if (loading) {
    return <div className="nfc-loading-spinner"><img src={Spinner} alt="Loading..." /></div>;
  }

  if (error) {
    return <div className="nfc-no-data-message">{error}</div>;
  }

  return (
    <>
      <h1 className="nfc-page-header">Contact Form Submissions</h1>
      {submissions.length > 0 ? (
        <>
          <div className="nfc-data-table-wrapper">
            <DataTable 
              data={submissions} 
              columns={columns} 
              onEdit={handleView} // Use handleView for the "edit" action
              onDelete={handleDeleteRequest}
              editButtonLabel="View" // Tell DataTable to label the button "View"
              tableClassName="nfc-data-table"
              actionButtonClasses={actionButtonClasses}
            />
          </div>
          <Pagination 
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            containerClassName="nfc-pagination"
            buttonClassName="nfc-page-btn"
            activeClassName="nfc-active"
          />
        </>
      ) : (
        <div className="nfc-no-data-message">No contact submissions found.</div>
      )}
      
      <Modal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        overlayClassName="nfc-modal-overlay"
        dialogClassName="nfc-modal-dialog"
        headerClassName="nfc-modal-header"
        titleClassName="nfc-modal-title"
        contentClassName="nfc-modal-content"
        footerClassName="nfc-modal-footer"
        confirmBtnClassName="nfc-modal-btn nfc-modal-confirm-btn"
        cancelBtnClassName="nfc-modal-btn nfc-modal-cancel-btn"
      >
        <p>Are you sure you want to delete this message? This action cannot be undone.</p>
      </Modal>
    </>
  );
};

export default ContactSubmissionsPage;